import { useEffect } from 'react';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import { Badge, Skeleton } from '@radix-ui/themes';
import { Link, useParams } from 'react-router-dom';
import FITUrls from 'helpers/FITUrls';
import { isNumeric } from 'helpers/utils';
import AdKaoraBanner, { AdSlot } from 'ui/design-base/AdKaoraBanner';
import Container from 'ui/design-base/Container';
import ErrorBox from 'ui/design-base/ErrorBox';
import NoMenuHeader from 'ui/design-base/NoMenuHeader';
import Page from 'ui/design-base/Page';
import { fetchCompetition } from 'store/competition/competitionSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { TournamentShortType } from 'types/tournament';

export const CompetitionPage = () => {
  const { id } = useParams<string>();
  const dispatch = useAppDispatch();
  const { competition, loading, error } = useAppSelector((state) => state.competition);
  useEffect(() => {
    dispatch(fetchCompetition(id));
  }, [id, dispatch]);

  const getCompetitionUrl = () => {
    if (!competition) return '';
    const { new_fit_competition_id, championship } = competition;

    if (championship && new_fit_competition_id) {
      return FITUrls.championship(new_fit_competition_id);
    } else {
      return FITUrls.newCompetition(new_fit_competition_id);
    }
  };

  const getCompetitionDescription = () =>
    `Pagina della competizione: ${competition?.name} - dal ${competition?.from} al ${
      competition?.to
    } - Circolo organizzatore: ${competition?.club?.name || ''} - regione: ${
      competition?.club?.region || ''
    } - provincia: ${competition?.club?.province || ''} - Lista tornei: ${competition?.tournaments?.map(
      (m: TournamentShortType) => m.name,
    )}`;

  const getChampionshipDescription = () =>
    `Pagina del campionato: ${competition?.name} - dal ${competition?.from} al ${
      competition?.to
    } - Lista fasi: ${competition?.tournaments?.map((m: TournamentShortType) => m.name)}`;

  const getPageKeyword = () =>
    `Pagina del campionato: ${competition?.name} | dal ${competition?.from} al ${
      competition?.to
    } | ${competition?.tournaments?.map((m: TournamentShortType) => `${m.name} | `)}`;

  const renderTournament = (tournament: TournamentShortType) => (
    <div
      key={tournament.id}
      id='container'
      className='border-neutral-neutral-2 hover:bg-accent-accent-3 flex flex-col items-start gap-2 self-stretch rounded-2xl border border-solid bg-white p-3'
    >
      <Link className='external-link' to={`/torneo/${tournament.id}`}>
        <div
          id='competition'
          className='flex w-full flex-row flex-wrap content-center items-center justify-between gap-3'
        >
          {isNumeric(tournament.players_count) && (
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div
                id='subscribers'
                className='border-neutral-neutral-2 text-neutral-neutral-12 flex items-center gap-3 rounded-xl border border-solid bg-white p-3 text-base font-medium'
              >
                <div className='text-neutral-neutral-8 text-sm font-medium'>
                  {competition?.championship ? 'Squadre Iscritte' : 'Iscritti'}
                </div>
                <div className='text-neutral-neutral-alpha-12 text-2xl font-bold'>{tournament.players_count}</div>
              </div>
            </Skeleton>
          )}
          <Skeleton loading={loading} className='h-auto w-auto'>
            <div id='competitionName' className='text-neutral-neutral-12 grow text-xl font-medium tracking-tighter'>
              {tournament.name === '' ? 'Torneo FITP' : tournament.name}
            </div>
          </Skeleton>

          {!competition?.championship && (
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div id='tournamentType' className='flex items-center'>
                <Badge size='3' variant='solid' className='bg-accent-accent-9 text-white'>
                  {tournament.age_category}
                </Badge>
              </div>
            </Skeleton>
          )}
          <Skeleton loading={loading} className='h-auto w-auto'>
            <div id='icon' className='flex justify-end self-end md:self-center'>
              <ArrowTopRightIcon width='18' height='18' />
            </div>
          </Skeleton>
        </div>
      </Link>
    </div>
  );
  const renderContent = () => {
    const itemClassName =
      'bg-white p-3 rounded-xl self-stretch border border-solid border-neutral-neutral-2 flex flex-col grow text-neutral-neutral-12 text-xl font-bold tracking-tight';
    const labelClassName = 'self-stretch text-neutral-neutral-8 text-sm font-medium tracking-normal';

    return (
      <>
        <NoMenuHeader
          {...competition}
          loading={loading}
          defaultName='Competizione FITP'
          fitpLink={getCompetitionUrl()}
        />
        <div className='border-neutral-neutral-2 bg-neutral-neutral-1 mt-2 flex flex-wrap items-stretch justify-between gap-2 rounded-2xl border border-solid p-3'>
          {!competition?.championship && competition?.club && (
            <div className={itemClassName}>
              <div className={labelClassName}>Circolo</div>
              <Skeleton loading={loading} className='h-auto w-auto'>
                <div className='underline'>
                  <Link to={`/circolo/${competition?.club.id}`}>{competition?.club.name}</Link>
                </div>
              </Skeleton>
            </div>
          )}
          {competition?.from && (
            <div className={itemClassName}>
              <div className={labelClassName}>Data Inizio</div>
              <Skeleton loading={loading} className='h-auto w-auto'>
                {competition?.from}
              </Skeleton>
            </div>
          )}
          {competition?.to && (
            <div className={itemClassName}>
              <div className={labelClassName}>Data Fine</div>
              <Skeleton loading={loading} className='h-auto w-auto'>
                {competition?.to}
              </Skeleton>
            </div>
          )}
        </div>

        <Container title={competition?.championship ? 'Fasi' : 'Tornei'} className='mt-2'>
          <div className='flex flex-col gap-2'>
            {competition?.tournaments?.map((tournament: TournamentShortType) => renderTournament(tournament))}
          </div>
        </Container>
      </>
    );
  };

  let pageTitle = 'Competizione';
  let content = renderContent();
  let pageKeywords;
  let pageDescription = '';

  if (!loading) {
    if (!error && competition) {
      pageTitle = competition.name;
      pageKeywords = getPageKeyword();
      pageDescription = competition?.championship ? getChampionshipDescription() : getCompetitionDescription();
    } else {
      content = (
        <div className='flex justify-center'>
          <ErrorBox
            errorType='Competizione non trovata'
            errorTitle='Qualcosa è andato storto nel caricamento di questa competizione'
            errorMessage='Prova più tardi. Se avrai nuovamente problemi, scrivici.'
          />
        </div>
      );
    }
  }

  return (
    <Page title={pageTitle} description={pageDescription || pageTitle} keywords={pageKeywords} showAds={true}>
      <AdKaoraBanner slot={AdSlot.ARTICLE_TOP} />
      {content}
      <AdKaoraBanner slot={AdSlot.ARTICLE_BOTTOM} />
    </Page>
  );
};

export default CompetitionPage;
